import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { Button, Text } from 'primitive-components';
import { colors } from 'bench-style-constants';
import { Modal } from 'modal-components';

const Layout = styled.div`
  display: grid;
  grid-row-gap: 16px;
`;

const List = styled.div``;
const ListItem = styled(Text)`
  padding-left: 22px;
  position: relative;
  &::before {
    content: '•';
    left: 0;
    position: absolute;
  }
`;

const TextWithEmphasis = styled(Text)`
  span {
    font-style: italic;
  }
`;

const Overlay = styled.div`
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
`;

const PrimaryButton = styled(Button)`
  color: ${colors.copper};
  &:hover {
    color: ${colors.pipe};
  }
  &:disabled {
    color: ${colors.smoke};
  }
`;
const SecondaryButton = styled(Button)``;

const SubscriptionCancellationModal = ({
  disablePrimaryButton,
  disableSecondaryButton,
  isSubscriptionCancelled,
  onClose,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
}) => (
  <Overlay onClick={event => event.currentTarget === event.target && onClose()}>
    {!isSubscriptionCancelled ? (
      <Modal size="large">
        <Modal.Header title="We’re here to help." onClose={onClose} />
        <Modal.Body>
          <Layout>
            <TextWithEmphasis>
              Whatever challenges your business faces, we’re here to support. If there is{' '}
              <span>anything</span> we can do to improve your experience with Bench, talk to us.
            </TextWithEmphasis>
            <Text>
              You can also put your account on hold, at no charge to you. We’ll pause all monthly
              bookkeeping and billing activities, but you’ll still have direct access to your
              bookkeeping team.
            </Text>
            <Text weight="bold">
              To pause your account or to discuss improvements we can make, send your bookkeeper a
              message or give us a call (888-760-1940).
            </Text>
            <Text>
              If you’re sure Bench is no longer the right fit for you, confirm your cancellation
              request below.
            </Text>
          </Layout>
        </Modal.Body>
        <Modal.Footer>
          <SecondaryButton
            data-test-id="subscription-cancellation-request-modal-secondary-button"
            disabled={disableSecondaryButton}
            onClick={onSecondaryButtonClick}
          >
            Go Back
          </SecondaryButton>
          <PrimaryButton
            data-test-id="subscription-cancellation-request-modal-primary-button"
            disabled={disablePrimaryButton}
            onClick={onPrimaryButtonClick}
          >
            Confirm Cancellation Request
          </PrimaryButton>
        </Modal.Footer>
      </Modal>
    ) : (
      <Modal size="large">
        <Modal.Header title="Take care of yourself." onClose={onClose} />
        <Modal.Body>
          <Layout>
            <Text>We’re sorry to see you go, but wish you all the best.</Text>
            <Text>
              You’ll hear from us within one business day to finalize your account closure and
              discuss next steps (eg. data exports).
            </Text>
            <Text>In the meantime:</Text>
            <List>
              <ListItem>You’ll no longer be billed for Bench services</ListItem>
              <ListItem>
                Your bookkeeping team will complete your current month of bookkeeping
              </ListItem>
            </List>
            <TextWithEmphasis>
              If <span>anything</span> changes, don’t hesitate to reach out. Whether we’re handling
              your books or not, we’ll be here to help you and your business succeed.
            </TextWithEmphasis>
          </Layout>
        </Modal.Body>
      </Modal>
    )}
  </Overlay>
);

SubscriptionCancellationModal.propTypes = {
  disablePrimaryButton: PropTypes.bool,
  disableSecondaryButton: PropTypes.bool,
  isSubscriptionCancelled: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onPrimaryButtonClick: PropTypes.func,
  onSecondaryButtonClick: PropTypes.func,
};

SubscriptionCancellationModal.defaultProps = {
  disablePrimaryButton: false,
  disableSecondaryButton: false,
  isSubscriptionCancelled: false,
  onPrimaryButtonClick: () => {},
  onSecondaryButtonClick: () => {},
};

export default SubscriptionCancellationModal;
